import React, { useCallback, useEffect, useMemo, useState } from 'react';
import config from '../../config';
import SoloShell from './SoloShell';
import { api } from './api';
import './BuzzerSolo.css';

export default function BuzzerSoloSettingsPage() {
  const [form, setForm] = useState({
    businessName: '',
    taxId: '',
    address: '',
    ownerName: '',
    phone: '',
    email: '',
    companySlug: '',
    clientBrandUrl: '',
    hasClientLogo: false,
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const privateLogoUrl = useMemo(() => `${config.API_BASE_URL}/buzzersolo/settings/logo?ts=${Date.now()}`, [form.hasClientLogo]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/buzzersolo/settings');
      setForm({
        businessName: data.businessName || '',
        taxId: data.taxId || '',
        address: data.address || '',
        ownerName: data.ownerName || '',
        phone: data.phone || '',
        email: data.email || '',
        companySlug: data.companySlug || '',
        clientBrandUrl: data.clientBrandUrl || '',
        hasClientLogo: !!data.hasClientLogo,
      });
      setMessage('');
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Failed to load Solo settings');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const saveSettings = async () => {
    try {
      setBusy(true);
      await api.put('/buzzersolo/settings', {
        businessName: form.businessName,
        taxId: form.taxId,
        address: form.address,
        ownerName: form.ownerName,
        phone: form.phone,
        email: form.email,
        clientBrandUrl: form.clientBrandUrl,
      });
      setMessage('Solo settings saved.');
      await load();
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Failed to save Solo settings');
    } finally {
      setBusy(false);
    }
  };

  const uploadLogo = async () => {
    if (!logoFile) return;
    try {
      setBusy(true);
      const payload = new FormData();
      payload.append('clientLogo', logoFile);
      await api.post('/buzzersolo/settings/logo', payload);
      setLogoFile(null);
      setLogoPreview('');
      setMessage('Logo uploaded.');
      await load();
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Failed to upload logo');
    } finally {
      setBusy(false);
    }
  };

  const removeLogo = async () => {
    try {
      setBusy(true);
      await api.delete('/buzzersolo/settings/logo');
      setLogoFile(null);
      setLogoPreview('');
      setMessage('Logo removed.');
      await load();
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Failed to remove logo');
    } finally {
      setBusy(false);
    }
  };

  const onLogoChange = (file) => {
    setLogoFile(file || null);
    if (!file) {
      setLogoPreview('');
      return;
    }
    const url = URL.createObjectURL(file);
    setLogoPreview(url);
  };

  return (
    <SoloShell title="Solo Settings" subtitle="Maintain invoice details and optional customer branding for the Solo public view.">
      {message ? <div className="bsolo-card" style={{ marginBottom: 16 }}>{message}</div> : null}

      <div className="bsolo-grid cols-2">
        <div className="bsolo-form-section">
          <h3>Invoice details</h3>
          <div className="bsolo-grid cols-2">
            <div className="bsolo-field">
              <label>Business / company name</label>
              <input value={form.businessName} onChange={(e) => setField('businessName', e.target.value)} />
            </div>
            <div className="bsolo-field">
              <label>VAT / ΑΦΜ</label>
              <input value={form.taxId} onChange={(e) => setField('taxId', e.target.value)} placeholder="Optional if you want to complete it later" />
            </div>
            <div className="bsolo-field" style={{ gridColumn: '1 / -1' }}>
              <label>Billing address</label>
              <input value={form.address} onChange={(e) => setField('address', e.target.value)} />
            </div>
            <div className="bsolo-field">
              <label>Owner / manager name</label>
              <input value={form.ownerName} onChange={(e) => setField('ownerName', e.target.value)} />
            </div>
            <div className="bsolo-field">
              <label>Phone</label>
              <input value={form.phone} onChange={(e) => setField('phone', e.target.value)} />
            </div>
            <div className="bsolo-field" style={{ gridColumn: '1 / -1' }}>
              <label>Email</label>
              <input type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} />
            </div>
            <div className="bsolo-meta" style={{ gridColumn: '1 / -1' }}>
              Login slug: <strong>{form.companySlug || '-'}</strong>
            </div>
          </div>
          <div className="bsolo-actions">
            <button className="bsolo-btn" onClick={saveSettings} disabled={busy}>{busy ? 'Saving...' : 'Save settings'}</button>
          </div>
        </div>

        <div className="bsolo-form-section">
          <h3>Customer view branding</h3>
          <div className="bsolo-field">
            <label>Venue URL (optional)</label>
            <input value={form.clientBrandUrl} onChange={(e) => setField('clientBrandUrl', e.target.value)} placeholder="https://yourvenue.gr" />
            <div className="bsolo-meta">If left empty, the public Solo page hides the website button.</div>
          </div>

          <div className="bsolo-upload-box" style={{ marginTop: 12 }}>
            <label style={{ display: 'block', fontWeight: 700, marginBottom: 8 }}>Venue logo image (optional)</label>
            <input type="file" accept="image/*" onChange={(e) => onLogoChange(e.target.files?.[0] || null)} />
            <div className="bsolo-meta" style={{ marginTop: 8 }}>If you skip the logo, the public page shows only the venue name.</div>

            <div className="bsolo-row" style={{ alignItems: 'center', marginTop: 14 }}>
              {logoPreview ? (
                <img src={logoPreview} alt="New logo preview" className="bsolo-logo-preview" />
              ) : form.hasClientLogo ? (
                <img src={privateLogoUrl} alt="Current venue logo" className="bsolo-logo-preview" />
              ) : (
                <div className="bsolo-meta">No logo uploaded yet.</div>
              )}
            </div>

            <div className="bsolo-actions">
              <button type="button" className="bsolo-btn-secondary" onClick={uploadLogo} disabled={!logoFile || busy}>Upload logo</button>
              <button type="button" className="bsolo-btn-secondary" onClick={removeLogo} disabled={!form.hasClientLogo || busy}>Remove logo</button>
            </div>
          </div>
        </div>
      </div>
    </SoloShell>
  );
}
